import {
  EcgDeviceLeadKey,
  EcgLeadKey,
  IRawEcgWaveform,
  IRawEcgWaveformDevice,
} from "../../@types/Ecg";

// Maps the device recorder's lead keys to the canonical clinical lead keys.
const DEVICE_LEAD_KEY: Record<EcgDeviceLeadKey, EcgLeadKey> = {
  leadI: "I",
  leadII: "II",
  leadIII: "III",
  aVR: "aVR",
  aVL: "aVL",
  aVF: "aVF",
};

// Canonical limb-lead order so the normalized waveform reads I, II, III, aVR…
const LEAD_ORDER: EcgLeadKey[] = ["I", "II", "III", "aVR", "aVL", "aVF"];

// The device JSON is identified by its `samples` map (the canonical format uses
// `waveform`). Anything already in the canonical shape is passed through.
function isDeviceWaveform(
  raw: IRawEcgWaveform | IRawEcgWaveformDevice
): raw is IRawEcgWaveformDevice {
  return (
    (raw as IRawEcgWaveformDevice).samples != null &&
    (raw as IRawEcgWaveform).waveform == null
  );
}

/**
 * Normalizes a raw ECG waveform into the canonical {@link IRawEcgWaveform}
 * shape. Accepts both the on-device recorder format (the current standard) and
 * the older SDK format — the latter is returned unchanged. This is the single
 * place that knows about the device JSON, so the rest of the pipeline only ever
 * deals with the canonical shape.
 */
export function normalizeEcgWaveform(
  raw: IRawEcgWaveform | IRawEcgWaveformDevice
): IRawEcgWaveform {
  if (!isDeviceWaveform(raw)) return raw;

  const waveform: Partial<Record<EcgLeadKey, number[]>> = {};
  (Object.keys(raw.samples) as EcgDeviceLeadKey[]).forEach((deviceKey) => {
    const key = DEVICE_LEAD_KEY[deviceKey];
    const samples = raw.samples[deviceKey];
    if (key && samples && samples.length > 0) waveform[key] = samples;
  });

  const leads = LEAD_ORDER.filter((key) => (waveform[key]?.length ?? 0) > 0);
  const frameCount = leads.reduce(
    (max, key) => Math.max(max, waveform[key]?.length ?? 0),
    0
  );

  return {
    schemaVersion: 0,
    sdkRecordUuid: raw.info?.recordingUuid ?? null,
    patientId: null,
    capturedAtIso: raw.info?.dateRecorded ?? "",
    source: {
      platform: raw.info?.phoneModel ?? null,
      appVersion: raw.info?.recorderSoftware ?? null,
      sdkVersion: raw.info?.recorderHardware ?? null,
    },
    sampleRateHz: raw.frequency > 0 ? raw.frequency : 300,
    leads,
    amplitudeUnit: "adc",
    // The device reports its ADC gain in units per mV — carry it so the parser
    // converts to mV correctly even if the gain ever differs from the legacy 2000.
    adcUnitsPerMv: raw.gain > 0 ? raw.gain : undefined,
    frameCount,
    waveform,
    heartRateBpm: null,
    batteryPercent: null,
    leadConfiguration: leads.length > 0 ? `${leads.length}L` : null,
  };
}
